/**
 * FileList Component
 *
 * Displays railML files in DIM UI's DataTable with:
 * - File metadata (subnetwork, shortname, created date, version, filesize)
 * - Download, copy-link and lineage action buttons
 * - Multi-select checkboxes for bulk zip download
 * - Client-side filtering (title, version, date range, newest-only)
 * - Column sorting, and columns that don't fit moved into expandable rows
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Button, Callout, Combobox, DataTable, Field, Menu, MenuItem, Select, SelectionBar, Switch, Tag, Text, ToggleChip, ToggleChipGroup, type ComboboxOption, type DataTableColumn } from '@bn-apps/dim-ui-react';
import { Download, ContentCopy, MoreVert, Schedule } from '@bn-apps/design-system-icons';
import styles from './FileList.module.scss';
import { getIntlLocale } from '@utils/locale.utils';
import { downloadFile, downloadFilesAsZip, formatFileSize } from '@services/files.service';
import { getRailMLFileLineage } from '@services/sparql.service';
import DataLineageModal from './DataLineageModal';
import type { RailMLFile, DataLineage } from '@/types';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface FileListProps {
  files: RailMLFile[] | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function getNewestPerGroup(items: RailMLFile[]): Map<string, RailMLFile> {
  const map = new Map<string, RailMLFile>();
  for (const f of items) {
    const key = `${f.title}::${f.schemaVersion}`;
    const existing = map.get(key);
    if (!existing || new Date(f.created).getTime() > new Date(existing.created).getTime()) {
      map.set(key, f);
    }
  }
  return map;
}

function getFilename(file: RailMLFile): string {
  return file.contentUrl.split('/').pop() || file.title;
}

function FileList({ files, loading, error, onRefresh }: FileListProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isPhone = useMediaQuery('(max-width: 480px)');
  const isTouch = useMediaQuery('(pointer: coarse)');
  const [selectedFileUris, setSelectedFileUris] = useState<Set<string>>(new Set());
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [lineageModalOpen, setLineageModalOpen] = useState<boolean>(false);
  const [currentLineage, setCurrentLineage] = useState<DataLineage | null>(null);
  const [lineageLoading, setLineageLoading] = useState<boolean>(false);
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const [filterTitles, setFilterTitles] = useState<Set<string>>(new Set());
  const [filterVersions, setFilterVersions] = useState<string[]>([]);
  const [filterDateRange, setFilterDateRange] = useState<string>('');
  const [newestOnly, setNewestOnly] = useState<boolean>(false);

  const locale = getIntlLocale(i18n.language);

  // Derive unique sorted title/description options for filter combobox
  const titleOptions: ComboboxOption[] = useMemo(() => {
    if (!files) return [];
    const titleDescMap = new Map<string, string>();
    for (const f of files) {
      if (!titleDescMap.has(f.title)) {
        titleDescMap.set(f.title, f.description);
      }
    }
    return [...titleDescMap.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([title, desc]) => ({ id: title, label: `${desc} (${title})` }));
  }, [files]);

  // Compute set of URIs for the latest file per shortname+version group
  const latestFileUris = useMemo((): Set<string> => {
    if (!files) return new Set();
    return new Set([...getNewestPerGroup(files).values()].map((f) => f.uri));
  }, [files]);

  // Client-side filtering
  const filteredData = useMemo((): RailMLFile[] => {
    if (!files) return [];
    let filtered = [...files];

    if (filterTitles.size > 0) {
      filtered = filtered.filter((f) => filterTitles.has(f.title));
    }
    if (filterVersions.length === 0) {
      return [];
    }
    filtered = filtered.filter((f) => filterVersions.includes(f.schemaVersion));
    if (filterDateRange) {
      const now = new Date();
      let cutoff: Date;
      switch (filterDateRange) {
        case 'lastDay':
          cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'lastWeek':
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30Days':
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoff = new Date(0);
      }
      filtered = filtered.filter((f) => new Date(f.created).getTime() >= cutoff.getTime());
    }
    if (newestOnly) {
      const newestUris = new Set([...getNewestPerGroup(filtered).values()].map((f) => f.uri));
      filtered = filtered.filter((f) => newestUris.has(f.uri));
    }
    return filtered;
  }, [files, filterTitles, filterVersions, filterDateRange, newestOnly]);

  // Derive unique schema versions for filter chips
  const availableVersions = useMemo((): string[] => {
    if (!files) return [];
    return [...new Set(files.map((f) => f.schemaVersion))].sort();
  }, [files]);

  // Initialize filter chips to all selected when versions become available
  useEffect(() => {
    if (availableVersions.length > 0 && filterVersions.length === 0) {
      setFilterVersions(availableVersions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableVersions]);

  // Only count and download the selected rows that the filters show
  const visibleSelected = useMemo(
    () => filteredData.filter((f) => selectedFileUris.has(f.uri)),
    [filteredData, selectedFileUris]
  );

  const isLoading = !files || loading;

  const handleDownloadSelected = useCallback(async (): Promise<void> => {
    if (!visibleSelected.length) return;
    setDownloadingZip(true);
    try {
      setDownloadError(null);
      await downloadFilesAsZip(visibleSelected, 'railml-files.zip');
    } catch (err) {
      console.error('[FileList] Zip download failed:', err);
      setDownloadError(err instanceof Error ? err.message : t('files.error.downloadFailed'));
    } finally {
      setDownloadingZip(false);
    }
  }, [visibleSelected, t]);

  // Action handlers
  const handleDownload = useCallback(async (fileData: RailMLFile): Promise<void> => {
    try {
      await downloadFile(fileData.contentUrl, getFilename(fileData));
    } catch (err) {
      console.error('[FileList] Download failed:', err);
      setDownloadError(err instanceof Error ? err.message : t('files.error.downloadFailed'));
    }
  }, [t]);

  const handleCopyLink = useCallback(async (fileData: RailMLFile): Promise<void> => {
    try {
      const fullUrl = new URL(`/download${fileData.contentUrl}`, window.location.origin).href;
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('[FileList] Copy link failed:', err);
    }
  }, []);

  const handleLineageClick = useCallback(async (fileData: RailMLFile): Promise<void> => {
    setCurrentFileName(fileData.title);
    setLineageModalOpen(true);
    setLineageLoading(true);
    setCurrentLineage(null);
    try {
      const lineage = await getRailMLFileLineage(fileData.uri);
      setCurrentLineage(lineage);
    } catch (err) {
      console.error('[FileList] Failed to fetch lineage:', err);
    } finally {
      setLineageLoading(false);
    }
  }, []);

  const formatDateTime = useCallback((dateValue: Date | string): string =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateValue)),
  [locale]);

  const columns = useMemo<DataTableColumn<RailMLFile>[]>(() => {
    const downloadButton = (file: RailMLFile) => (
      <button
        type="button"
        className={styles.iconButton}
        aria-label={t('files.download.aria', { filename: getFilename(file) })}
        title={t('files.download.button')}
        onClick={() => void handleDownload(file)}
      >
        <Download />
      </button>
    );
    const moreButtons = (file: RailMLFile) => (
      <>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t('files.download.copyLinkAria', { filename: getFilename(file) })}
          title={t('files.download.copyLink')}
          onClick={() => void handleCopyLink(file)}
        >
          <ContentCopy />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t('files.dataLineage.buttonAria', { filename: getFilename(file) })}
          title={t('files.dataLineage.button')}
          onClick={() => void handleLineageClick(file)}
        >
          <Schedule />
        </button>
      </>
    );

    const latestTag = (file: RailMLFile) =>
      latestFileUris.has(file.uri) && <Tag tone="info" className={styles.latestTag}>{t('files.tag.latest')}</Tag>;

    // Narrow: the short name takes the long name's place, which moves to a second line
    const nameColumns: DataTableColumn<RailMLFile>[] = isMobile ? [
      {
        id: 'title',
        header: t('files.table.shortname'),
        minWidth: 140,
        sortValue: (file) => file.title,
        cell: (file) => (
          <span className={styles.nameCell} title={file.description}>
            <span className={styles.descriptionCell}>
              <span className={styles.descriptionName}>{file.title}</span>
              {latestTag(file)}
            </span>
            <span className={styles.nameSecondary}>{file.description}</span>
          </span>
        ),
      },
    ] : [
      {
        id: 'description',
        header: t('files.table.description'),
        minWidth: 160,
        sortValue: (file) => file.description,
        cell: (file) => (
          <span className={styles.descriptionCell}>
            <span className={styles.descriptionName} title={file.description}>{file.description}</span>
            {latestTag(file)}
          </span>
        ),
      },
      {
        id: 'title',
        header: t('files.table.shortname'),
        width: '8rem',
        minWidth: 100,
        priority: 3,
        sortValue: (file) => file.title,
        cell: (file) => file.title,
      },
    ];

    const dataColumns: DataTableColumn<RailMLFile>[] = [
      ...nameColumns,
      {
        id: 'created',
        header: t('files.table.created'),
        width: '11rem',
        minWidth: 150,
        priority: 2,
        sortValue: (file) => new Date(file.created),
        cell: (file) => formatDateTime(file.created),
      },
      {
        id: 'schemaVersion',
        header: t('files.table.version'),
        width: '5rem',
        minWidth: 80,
        priority: 4,
        sortValue: (file) => file.schemaVersion,
        cell: (file) => file.schemaVersion,
      },
      {
        id: 'filesize',
        header: t('files.table.filesize'),
        width: '7rem',
        minWidth: 100,
        priority: 1,
        numeric: true,
        sortValue: (file) => Number(file.filesize),
        cell: (file) => formatFileSize(file.filesize),
      },
    ];

    // On phones and touch screens the three buttons would be 44px each: they go into one menu
    if (isPhone || isTouch) {
      return [
        ...dataColumns,
        {
          id: 'actions',
          header: t('files.table.actions'),
          hideHeader: true,
          width: '3.5rem',
          minWidth: 56,
          cell: (file) => (
            <Menu
              label={t('files.table.moreActions')}
              placement="bottom end"
              trigger={
                <Button variant="tertiary" size="sm" hideText iconStart={MoreVert}>
                  {t('files.table.moreActions')}
                </Button>
              }
            >
              <MenuItem id="download" icon={Download} onAction={() => void handleDownload(file)}>
                {t('files.download.button')}
              </MenuItem>
              <MenuItem id="copy" icon={ContentCopy} onAction={() => void handleCopyLink(file)}>
                {t('files.download.copyLink')}
              </MenuItem>
              <MenuItem id="lineage" icon={Schedule} onAction={() => void handleLineageClick(file)}>
                {t('files.dataLineage.button')}
              </MenuItem>
            </Menu>
          ),
        },
      ];
    }
    return [
      ...dataColumns,
      {
        id: 'actions',
        header: t('files.table.actions'),
        hideHeader: true,
        width: '10.5rem',
        minWidth: 168,
        cell: (file) => (
          <div className={styles.actionButtons}>
            {downloadButton(file)}
            {moreButtons(file)}
          </div>
        ),
      },
    ];
  }, [t, isMobile, isPhone, isTouch, latestFileUris, formatDateTime, handleDownload, handleCopyLink, handleLineageClick]);

  const selectionBar = (
    <SelectionBar
      count={isLoading ? 0 : visibleSelected.length}
      label={(count) => t('files.selection.selectedCount', { count })}
      clearLabel={t('common.clear')}
      onClear={() => setSelectedFileUris(new Set())}
      actions={
        <Button
          className={styles.zipButton}
          variant="primary"
          size="sm"
          iconStart={Download}
          isBusy={downloadingZip}
          onClick={handleDownloadSelected}
        >
          {t('files.selection.downloadZip')}
        </Button>
      }
    />
  );

  const filterControls = (
    <>
      <div className={styles.filterSubnetwork}>
        <Combobox
          id="filter-title"
          label={t('files.table.description')}
          selectionMode="multiple"
          options={titleOptions}
          selectedKeys={filterTitles}
          onSelectionChange={setFilterTitles}
          placeholder={t('combobox.placeholder')}
        />
      </div>
      <div className={styles.filterVersion}>
        <label className={styles.filterLabel}>
          {t('files.table.version')}
        </label>
        <ToggleChipGroup
          label={t('files.filters.version-aria')}
          size="sm"
          selectedKeys={new Set(filterVersions)}
          onSelectionChange={(keys) => setFilterVersions([...keys])}
        >
          {availableVersions.map((version) => (
            <ToggleChip key={version} id={version}>
              {t('files.filters.railMLversion', { version })}
            </ToggleChip>
          ))}
        </ToggleChipGroup>
      </div>
      <div className={styles.filterCreated}>
        <Field label={t('files.filters.dateRange')}>
          <Select
            value={filterDateRange}
            onChange={(e) => setFilterDateRange(e.target.value)}
          >
            <option value="">{t('common.all')}</option>
            <option value="lastDay">{t('files.filters.lastDay')}</option>
            <option value="lastWeek">{t('files.filters.lastWeek')}</option>
            <option value="last30Days">{t('files.filters.last30Days')}</option>
          </Select>
        </Field>
      </div>
      <div className={styles.filterLatest}>
        <Switch
          label={t('files.tag.latest')}
          checked={newestOnly}
          onChange={(e) => setNewestOnly(e.target.checked)}
        />
      </div>
    </>
  );

  return (
    <div className="bn-mbe-1600">

      {downloadError && (
        <Callout
          status="critical"
          role="alert"
          title={t('files.error.downloadFailed')}
          onDismiss={() => setDownloadError(null)}
        >
          {downloadError}
        </Callout>
      )}

      {/* Filter bar - accordion on mobile, grid on desktop */}
      {isMobile ? (
        <>
          <div className={styles.filterAccordionWrapper}>
            <Accordion>
              <AccordionItem title={t('files.filters.title')}>
                <div className={styles.filterBarMobile}>
                  {filterControls}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
          {visibleSelected.length > 0 && selectionBar}
        </>
      ) : (
        <div className={styles.filterBar}>
          {filterControls}
          <div className={styles.filterZip}>{selectionBar}</div>
        </div>
      )}

      <div className={styles.rowCountBar}>
        <Text style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          {t('files.count', { count: filteredData.length })}
        </Text>
      </div>

      <DataTable
        label={t('files.table.aria.label')}
        columns={columns}
        rows={filteredData}
        rowKey={(file) => file.uri}
        rowLabel={getFilename}
        defaultSort={{ column: 'created', direction: 'descending' }}
        selectionMode="multiple"
        selectedKeys={selectedFileUris}
        onSelectionChange={setSelectedFileUris}
        selectOnRowClick
        maxHeight="600px"
        isLoading={isLoading}
        empty={files?.length ? t('files.empty.withFilter') : t('files.empty.noFiles')}
        error={error && t('files.error.loadingFiles')}
        onRetry={onRefresh}
        labels={{
          selectAll: t('files.selection.selectAll'),
          selectRow: (filename) => t('files.selection.selectRow', { filename }),
          details: t('files.table.details'),
          showDetails: (filename) => t('files.table.expandRow', { filename }),
          retry: t('common.retry'),
        }}
      />

      <DataLineageModal
        isOpen={lineageModalOpen}
        onClose={() => setLineageModalOpen(false)}
        lineage={currentLineage}
        loading={lineageLoading}
        fileName={currentFileName}
      />
    </div>
  );
}

export default FileList;
